import { useCallback, useMemo } from 'react'
import { formatClock } from '@racedash/shared'
import { describeDriver, enteredDrivers } from '../../dashboard/screenContext'
import { Menu } from '@mantine/core'
import {
  applyCarFilter,
  byCarNumber,
  carsInView,
  defaultCarFilter,
  formatDuration,
  formatLapTime,
  isCaptureLive,
  parseCarFilter,
  pruneToClass,
  resolveDriver,
  stintAverageMs,
  stintVendorDriver,
  type CarFilter,
  type Stint,
  type StintsDoc,
} from '@racedash/shared'
import type { DashboardData } from '../../dashboard/useSessionData'
import type { ScreenContext } from '../../dashboard/screenContext'
import { useLocalSetting } from '../../lib/useLocalSetting'
import { usePortalTarget } from '../../lib/usePortalTarget'
import { axisWidth, hourTicks, pct, spanning, type Span } from '../timeAxis'
import CarFilterBar from './CarFilterBar'
import './stints.css'

/**
 * Every car's stints on one time axis.
 *
 * Interrogable rather than glanceable: you sit down with this, so it scrolls
 * both ways and packs information rather than scaling to fill a screen.
 *
 * The cards are laid out proportionally to real time, which is the whole point
 * — a stint that ran long is a wide card, and a column of cards ending at the
 * same place is a field that all stopped together.
 */
export default function StintsScreen({
  data,
  car,
  now,
  context,
  screenId,
}: {
  data: DashboardData
  car: string | null
  now: Date
  context: ScreenContext
  screenId: string
}) {
  const all = useMemo(() => buildRows(data.stints), [data.stints])

  /*
   * Whether the stream behind this session is still being captured.
   *
   * The one thing that decides whether the last stint of each car is still
   * running. It is measured against the BROWSER's clock, not data.clock's:
   * ServerClock takes its offset from lastSeenAt, so on a session captured in
   * May it reports a "now" in May, and lastSeenAt would look seconds old
   * forever. That is precisely how a fifteen-minute stint came to be drawn as
   * a thousand hours.
   */
  const live = isCaptureLive(data.session?.lastSeenAt?.toDate(), now)

  // Where the chart stops. The last lap anybody completed is the real end of
  // racing; finishedAt, where the stream gave us one, is the flag itself and
  // comes a little after. Not lastSeenAt, which is when capture stopped — nine
  // hours later on one of these races.
  const endedAt = data.session?.finishedAt?.toDate() ?? null

  // Class comes from the standings and the rows come from the stints, because
  // those are different populations: a car that has retired keeps its stints
  // and leaves the timing, and dropping it from the chart the moment it stops
  // is the opposite of what a stint chart is for.
  const cars = useMemo(() => {
    const classes = new Map<string, string | undefined>()
    for (const c of data.standings?.cars ?? []) classes.set(c.number, c.class)
    return carsInView(all.map((r) => r.car), (n) => classes.get(n), context.ourCars)
  }, [all, data.standings, context.ourCars])

  const fallback = useMemo(() => defaultCarFilter(cars), [cars])
  const [filter, setFilter] = useLocalSetting<CarFilter>(
    `racedash.stints.${context.viewId}.${screenId}.cars`,
    fallback,
    parseCarFilter,
  )

  const change = useCallback(
    (next: CarFilter) => setFilter(pruneToClass(cars, next)),
    [cars, setFilter],
  )

  const shown = useMemo(() => {
    const keep = new Set(applyCarFilter(cars, filter).map((c) => c.number))
    return all.filter((r) => keep.has(r.car))
  }, [all, cars, filter])

  const rows = shown
  const span = useMemo(
    () => extent(rows, data.asAt(now), live, endedAt),
    [rows, data, now, live, endedAt],
  )

  if (all.length === 0) {
    return (
      <div className="stints-empty">
        <p>No stints yet. They appear once cars start completing laps.</p>
      </div>
    )
  }

  const ticks = hourTicks(span)

  return (
    <div className="stints">
      <CarFilterBar
        cars={cars}
        filter={filter}
        onChange={change}
        shown={rows.length}
        onReset={() => setFilter(fallback)}
      />
      {rows.length === 0 ? (
        <div className="stints-empty">
          <p>Nothing matches this filter. Widen it above to see the rest of the field.</p>
        </div>
      ) : (
      <div className="stints-scroll">
        <div className="stints-grid" style={{ ['--stint-width' as string]: `${axisWidth(span)}px` }}>
          {/* Time of day across the top, so a card can be read against the
              clock rather than only against its neighbours. */}
          <div className="stints-axis">
            <div className="stints-axis-label" />
            <div className="stints-axis-track">
              {ticks.map((t) => (
                <div
                  key={t.at}
                  className="stints-tick"
                  style={{ left: `${pct(t.at, span)}%` }}
                  data-major={t.major || undefined}
                >
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* The track-state band. fcyPeriods' first consumer, and the reason a
              slow stint is explicable rather than mysterious. */}
          <div className="stints-band">
            <div className="stints-axis-label">Track</div>
            <div className="stints-axis-track stints-band-track">
              {data.fcyPeriods.map((p, i) => {
                const from = p.startedAt?.toDate().getTime()
                const to = p.endedAt?.toDate().getTime() ?? span.to
                if (from == null) return null
                return (
                  <div
                    key={i}
                    className="stints-fcy"
                    data-kind={(p.state ?? '').toLowerCase()}
                    style={{ left: `${pct(from, span)}%`, width: `${pct(to, span) - pct(from, span)}%` }}
                    title={`${p.state ?? 'FCY'} ${formatClock(new Date(from), { seconds: true })}`}
                  />
                )
              })}
            </div>
          </div>

          {rows.map((row) => (
            <div className="stints-row" key={row.car} data-mine={car === row.car || undefined}>
              <div className="stints-axis-label stints-car">{row.car}</div>
              <div className="stints-axis-track">
                {row.stints.map((s) => {
                  const from = start(s)
                  // Nothing dates this stint, so there is nowhere on the axis
                  // to put it. Drawn at the left edge it would read as a car
                  // that ran at the start of the session and stopped.
                  if (from == null) return null
                  const to = end(s, data.asAt(now), live)
                  const left = pct(from, span)
                  const right = pct(to, span)
                  return (
                    <StintCard
                      key={s.number}
                      stint={s}
                      car={row.car}
                      left={left}
                      width={Math.max(right - left, 0.4)}
                      lengthMs={to - from}
                      startedAt={new Date(from)}
                      running={!!s.open && live}
                      context={context}
                    />
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  )
}

function StintCard({
  stint,
  car,
  left,
  width,
  lengthMs,
  startedAt,
  running,
  context,
}: {
  stint: Stint
  car: string
  left: number
  width: number
  lengthMs: number
  startedAt: Date
  /** The car's latest stint AND the stream still being captured. */
  running: boolean
  context: ScreenContext
}) {
  const avg = stintAverageMs(stint, 'green')
  const vendor = stintVendorDriver(stint)
  // Colour cycles by stint NUMBER, not per car, so stint three is the same
  // colour on every row. That makes the columns line up as a fleet, which is
  // most of the reason to stack the cars in the first place — "that car is a
  // stop behind" is then visible without reading anything.
  // Green is for the stint happening now, which a finished session has none
  // of — its last stints are history like every other.
  const tone = running ? 'open' : `c${stint.number % 4}`

  const portal = usePortalTarget()
  const resolved = resolveDriver(context.declarations, car, startedAt, vendor.name ?? undefined)
  // The vendor's own spelling for a vendor answer, unlike the driver tile. Every
  // other car on this chart can only ever show what the timing system wrote,
  // and one row in full names among thirty in T.QUA form reads as a different
  // kind of fact rather than a better one. Declared drivers keep the roster
  // name: that is a different claim, and the tick says so.
  const driver =
    resolved.source === 'vendor'
      ? { name: resolved.name, source: 'vendor' as const }
      : describeDriver(context, car, resolved)
  // Editable only for a car this organisation actually entered. Declaring a
  // driver for somebody else's car is a thing the data model permits — two
  // teams may hold different beliefs about one car — but not a thing anybody
  // means to do from a chart.
  const mine = context.declare && context.ourCars.includes(car)

  /*
   * Who this menu offers.
   *
   * The drivers entered for THIS car at this event, not the whole team list. A
   * club team's roster is everybody who has ever raced for them; the four
   * people in the car this weekend are the only plausible answers, and picking
   * from four is a glance where picking from thirty is a search.
   *
   * Falls back to the full roster when the entry names nobody, because an empty
   * menu would make the feature look broken rather than make the entry look
   * incomplete.
   */
  const choices = enteredDrivers(context, car)

  const head = (
    <div className="stint-head" data-editable={mine || undefined}>
      #{stint.number + 1} {driver.name ?? '—'}
      {/* Whose answer this is. The team's own declaration and the timing
          system's guess are different claims, and a stint chart is exactly
          where the difference gets acted on. */}
      {driver.source === 'team' && (
        <span className="stint-source" title="Your team said so">
          ✓
        </span>
      )}
      {driver.source === 'vendor' && !vendor.certain && (
        <span
          className="stint-doubt"
          title="The timing system named more than one driver during this stint"
        >
          ?
        </span>
      )}
      {running && <span className="stint-current"> (current)</span>}
    </div>
  )

  return (
    <div
      className="stint-card"
      data-tone={tone}
      style={{ left: `${left}%`, width: `${width}%` }}
      // Everything on the card, for the cards too narrow to show any of it.
      title={[
        `Stint ${stint.number + 1}${running ? ' (current)' : ''}: ${driver.name ?? 'driver unknown'}${
          driver.source === 'team' ? ' (your team said so)' : driver.source === 'vendor' && !vendor.certain ? ' (timing system unsure)' : ''
        }`,
        `Laps ${stint.firstLap}–${stint.lastLap}, ${formatDuration(lengthMs)}${running ? ' so far' : ''}`,
        `Average ${avg == null ? '—' : formatLapTime(avg)}, best ${formatLapTime(stint.bestLapMs)}`,
      ].join('\n')}
    >
      {mine ? (
        <Menu
          shadow="md"
          width={220}
          position="bottom-start"
          withinPortal
          // Into the fullscreen element when there is one, or this menu is
          // mounted somewhere the browser is not painting.
          portalProps={{ target: portal }}
        >
          <Menu.Target>
            <button type="button" className="stint-head-button">
              {head}
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              Who drove stint {stint.number + 1}
              {running ? ' (current)' : ''}?
            </Menu.Label>
            {choices.map((d) => (
              <Menu.Item
                key={d.id}
                onClick={() => void context.declare?.(car, d.id, startedAt)}
              >
                {d.name}
              </Menu.Item>
            ))}
            {choices.length === 0 && (
              <Menu.Item disabled>No drivers in your team list yet</Menu.Item>
            )}
            <Menu.Divider />
            {/* A real answer, and not the same as saying nothing: it hands the
                question back to the timing system rather than asserting an
                empty one. */}
            <Menu.Item onClick={() => void context.declare?.(car, null, startedAt)}>
              We don&rsquo;t know
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        head
      )}
      <div className="stint-line">Avg: {avg == null ? '—' : formatLapTime(avg)}</div>
      <div className="stint-line">Best: {formatLapTime(stint.bestLapMs)}</div>
      <div className="stint-foot">
        {formatDuration(lengthMs)}
        {running && '+'}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ maths

interface Row {
  car: string
  stints: Stint[]
}

function buildRows(docs: StintsDoc[]): Row[] {
  const rows = docs
    .filter((d) => (d.stints?.length ?? 0) > 0)
    .map((d) => ({ car: d.car, stints: d.stints ?? [] }))
  // By car number read as a number where it is one, so 7 comes before 58 and
  // before 436 — lexical order would put 111 before 7.
  rows.sort((a, b) => byCarNumber(a.car, b.car))
  return rows
}


/*
 * When a stint began, or null when nothing says.
 *
 * Null is a real answer, not a missing one. A car can have a single lap
 * document with no instant on it at all — seen on the timing, never seen to
 * complete — and a stint built from only that cannot be placed on a time axis.
 * The old fallback was 0, which is not "unknown" to a chart, it is 1970.
 */
function start(s: Stint): number | null {
  const at = s.startedAt?.toDate().getTime()
  return at == null || !Number.isFinite(at) ? null : at
}

/*
 * When a stint ended.
 *
 * The car's latest stint runs to now only while the stream is still being
 * captured — that is what makes its card grow and its length carry a "+".
 * Otherwise every stint ends where it ended, which for the last one is the
 * completion of the last lap the car managed.
 *
 * That covers retirement without having to detect it. A car that pits and never
 * re-emerges, or leaves on the back of a truck and never triggers the loop
 * again, simply has no more laps — so its bar stops where its evidence stops,
 * which is both the truthful answer and a visible one.
 *
 * The final `?? now` is for stints written before the interpreter kept the end
 * of the last one. Reprocessing removes them; until then they are bounded by
 * the chart's own extent rather than left to run away.
 */
function end(s: Stint, now: Date, live: boolean): number {
  if (s.open && live) return now.getTime()
  return s.endedAt?.toDate().getTime() ?? now.getTime()
}

function extent(rows: Row[], now: Date, live: boolean, finishedAt: Date | null): Span {
  const instants: number[] = []
  for (const row of rows) {
    for (const s of row.stints) {
      const from = start(s)
      if (from == null) continue
      instants.push(from)
      instants.push(end(s, now, live))
    }
  }
  // So the axis reaches the flag rather than the last car to cross the line,
  // which are not quite the same instant.
  if (finishedAt) instants.push(finishedAt.getTime())
  return spanning(instants, now)
}
